'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { CircleNotch } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  [
    // Reset
    'inline-flex items-center justify-center whitespace-nowrap',
    // Typography
    'text-sm font-semibold',
    // Скругление живёт в size, а НЕ здесь. Причина техническая: cn() собран на
    // tailwind-merge 2.6, который про rounded-card / rounded-pill ничего не знает
    // и не схлопывает их между собой. База «rounded-card» плюс «rounded-pill» из
    // размера дали бы ДВА класса на элементе, и победил бы не тот, что написан
    // правее, а тот, что стоит ниже в собранном CSS. Поэтому у каждого размера
    // ровно одна ступень скругления и склеивать нечего.
    // Transitions - ТОЛЬКО CSS, никаких JS анимаций
    'transition-all duration-200 ease-out',
    // Focus
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]',
    // Disabled
    'disabled:pointer-events-none disabled:opacity-50',
    // Press effect
    'active:scale-[0.97]',
    // SVG
    '[&_svg]:shrink-0 [&_svg]:size-[1.125em]',
    // Gap between icon and text
    'gap-2',
  ],
  {
    variants: {
      variant: {
        // Primary - залитая кнопка
        // shadow-[var(--primary)]/25 отсюда убран как мёртвый код, а не как
        // «уборка фиолетового». Проверено сборкой: эта запись кладёт цвет в
        // --tw-shadow (саму тень), а не в --tw-shadow-color, и соседний
        // shadow-sm объявлен в CSS ниже и её перетирает. Фиолетового здесь
        // никогда и не было — рисовалась обычная чёрная тень.
        default: [
          'bg-[var(--primary)] text-white',
          'shadow-raised',
          'hover:bg-[var(--primary-hover)] hover:shadow-float',
          'active:bg-[var(--primary-active)]',
        ],
        // Secondary - мягкий фон
        secondary: [
          'bg-[var(--primary-subtle)] text-[var(--primary)]',
          'hover:bg-[var(--primary-muted)]',
        ],
        // Outline
        outline: [
          'border border-[var(--border-default)] bg-transparent text-[var(--text-primary)]',
          'hover:border-[var(--primary)] hover:bg-[var(--primary-subtle)] hover:text-[var(--primary)]',
        ],
        // Ghost
        ghost: [
          'text-[var(--text-secondary)]',
          'hover:bg-[var(--primary-subtle)] hover:text-[var(--primary)]',
        ],
        // Destructive
        destructive: [
          'bg-[var(--error)] text-white',
          'hover:bg-[var(--error)]/90',
          'shadow-raised',
        ],
        // Success
        success: [
          'bg-[var(--success)] text-white',
          'hover:bg-[var(--success)]/90',
          'shadow-raised',
        ],
        // Glow — фирменный фиолетовый ореол. Ответ 35 = Б: свечение СОХРАНЯЕТСЯ
        // и живёт отдельной ручкой, чтобы его нельзя было вычистить заодно с
        // уплощением глубины. Роль shadow-glow пишет box-shadow напрямую, поэтому
        // класть рядом ещё одну тень нельзя — победит объявленная в CSS ниже.
        // Кольцо фокуса при этом не теряется: focus-visible:* сортируется в самый
        // конец файла и перебивает свечение (проверено сборкой).
        glow: [
          'bg-[var(--primary)] text-white',
          'shadow-glow',
          'hover:shadow-glow-lg hover:bg-[var(--primary-hover)]',
        ],
        // Link
        link: [
          'text-[var(--primary)] underline-offset-4',
          'hover:underline',
          'p-0 h-auto',
        ],
      },
      // Кнопка с подписью — «широкая кнопка» = ступень card.
      // Кнопка-иконка — по контракту ступень pill.
      size: {
        xs: 'h-7 px-2.5 text-xs rounded-card',
        sm: 'h-8 px-3 text-xs rounded-card',
        default: 'h-10 px-4 rounded-card',
        lg: 'h-11 px-5 rounded-card',
        xl: 'h-12 px-6 text-base rounded-card',
        icon: 'h-10 w-10 p-0 rounded-pill',
        'icon-sm': 'h-8 w-8 p-0 rounded-pill',
        'icon-lg': 'h-12 w-12 p-0 rounded-pill',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        data-button-variant={variant || 'default'}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <CircleNotch className="animate-spin" weight="bold" />
        ) : leftIcon ? (
          leftIcon
        ) : null}
        
        {children}
        
        {!isLoading && rightIcon}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };

